import { ChevronUp, ChevronDown, Loader2, FileText, Info } from "lucide-react";
import { cn } from "@/lib/utils/helpers";
import type { ApplicationTableRow } from "@/features/applicant/types/utils";
import type { ApplicantApplicationsTableProps } from "@/features/applicant/types/components";

function ApplicantApplicationsTableSortIcon({
  column,
  sortColumn,
  sortOrder,
}: {
  column: string;
  sortColumn: string | null;
  sortOrder: "asc" | "desc";
}) {
  if (sortColumn !== column) {
    return (
      <div className="flex flex-col -space-y-1">
        <ChevronUp className="h-3 w-3 text-gray-400" />
        <ChevronDown className="h-3 w-3 text-gray-400" />
      </div>
    );
  }
  return sortOrder === "asc" ? (
    <ChevronUp className="h-4 w-4 text-blue-600" />
  ) : (
    <ChevronDown className="h-4 w-4 text-blue-600" />
  );
}

function StatusCell({
  row,
  testIdPrefix,
  rowTestSuffix,
  onShowDenialReasons,
}: {
  row: ApplicationTableRow;
  testIdPrefix: string;
  rowTestSuffix: string;
  onShowDenialReasons: (applicantId: string | number) => void;
}) {
  return (
    <td className="px-4 sm:px-6 py-3 sm:py-4 whitespace-nowrap">
      <div className="flex items-center gap-2">
        <span
          className={cn(
            "inline-flex items-center px-3 py-1.5 text-xs font-semibold rounded-full",
            row.status.toUpperCase().includes("CANCELLED") ||
              row.status.toUpperCase() === "DENIED" ||
              row.status.toUpperCase() === "EXPIRED" ||
              row.status.toUpperCase() === "VOID"
              ? "bg-red-100 text-red-800"
              : row.status.toUpperCase() === "INCOMPLETE" ||
                  row.status.toUpperCase() === "AWAITING" ||
                  row.status.toUpperCase() === "NOT_STARTED" ||
                  row.status.toUpperCase() === "NOT STARTED"
                ? "bg-yellow-100 text-yellow-800"
                : row.status.toUpperCase() === "COMPLETE" ||
                    row.status.toUpperCase() === "APPROVED" ||
                    row.status.toUpperCase() === "PROPERTY APPROVED" ||
                    row.status.toUpperCase() === "PROPERTY_APPROVED"
                  ? "bg-green-100 text-green-800"
                  : row.status.toUpperCase() === "PENDING" ||
                      row.status.toUpperCase().includes("SENT")
                    ? "bg-blue-100 text-blue-800"
                    : "bg-gray-100 text-gray-800",
          )}
        >
          {row.status}
        </span>
        {row.status.toUpperCase() === "DENIED" && row.applicantId && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onShowDenialReasons(row.applicantId!);
            }}
            data-testid={`${testIdPrefix}-denial-reasons-btn-${rowTestSuffix}`}
            className="text-red-500 hover:text-red-700 transition-colors p-1 rounded-full hover:bg-red-50"
            title="Click for detail"
          >
            <Info className="h-5 w-5" />
          </button>
        )}
      </div>
    </td>
  );
}

function ActionsCell({
  row,
  testIdPrefix,
  rowTestSuffix,
  loadingRowId,
  isAnyButtonLoading,
  getRowKey,
  getButtonConfig,
  onStartContinue,
  onView,
}: {
  row: ApplicationTableRow;
  testIdPrefix: string;
  rowTestSuffix: string;
  loadingRowId: string | number | null;
  isAnyButtonLoading: boolean;
  getRowKey: (row: ApplicationTableRow) => string | number;
  getButtonConfig: (row: ApplicationTableRow) => { text: string; className: string };
  onStartContinue: (row: ApplicationTableRow) => void;
  onView: (row: ApplicationTableRow) => void;
}) {
  const buttonConfig = getButtonConfig(row);

  return (
    <td className="px-4 sm:px-6 py-3 sm:py-4 whitespace-nowrap text-sm">
      {row.activeButton === "START_NEW" && (
        <button
          onClick={() => onStartContinue(row)}
          disabled={isAnyButtonLoading}
          data-testid={`${testIdPrefix}-start-btn-${rowTestSuffix}`}
          className={buttonConfig.className}
        >
          {loadingRowId === getRowKey(row) ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin inline-block mr-1" />
              Opening...
            </>
          ) : (
            buttonConfig.text
          )}
        </button>
      )}
      {row.activeButton === "CONTINUE" && (
        <button
          onClick={() => onStartContinue(row)}
          disabled={isAnyButtonLoading}
          data-testid={`${testIdPrefix}-continue-btn-${rowTestSuffix}`}
          className={buttonConfig.className}
        >
          {loadingRowId === getRowKey(row) ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin inline-block mr-1" />
              Opening...
            </>
          ) : (
            buttonConfig.text
          )}
        </button>
      )}
      {row.activeButton === "VIEW" && (
        <button
          onClick={() => onView(row)}
          disabled={isAnyButtonLoading}
          data-testid={`${testIdPrefix}-view-btn-${rowTestSuffix}`}
          className={buttonConfig.className}
        >
          {loadingRowId === getRowKey(row) ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin inline-block mr-1" />
              Loading...
            </>
          ) : (
            buttonConfig.text
          )}
        </button>
      )}
    </td>
  );
}

export function ApplicantApplicationsTable({
  title,
  testIdPrefix,
  variant = "standard",
  rows,
  isLoading,
  sortColumn,
  sortOrder,
  onSort,
  loadingRowId,
  isAnyButtonLoading,
  emptyTitle,
  emptySubtitle,
  onStartContinue,
  onView,
  onShowDenialReasons,
  getRowKey,
  getButtonConfig,
}: ApplicantApplicationsTableProps) {
  const isPreScreening = variant === "preScreening";
  const columnCount = 8;

  const thClass =
    "px-4 sm:px-6 py-4 sm:py-6 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider";
  const thSortClass = `${thClass} cursor-pointer hover:bg-gray-100 transition-colors`;

  return (
    <section className="mb-8" data-testid={`${testIdPrefix}-section`}>
      <h2 className="text-lg sm:text-xl font-semibold text-gray-900 mb-4">{title}</h2>
      <div
        data-testid={`${testIdPrefix}-table-card`}
        className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden"
      >
        <div className="overflow-x-auto touch-scroll">
          <table data-testid={`${testIdPrefix}-table`} className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th
                  data-testid={`${testIdPrefix}-table-sort-invite-date`}
                  className={thSortClass}
                  onClick={() => onSort("invite_date")}
                >
                  <div className="flex items-center gap-2">
                    Invite Date
                    <ApplicantApplicationsTableSortIcon column="invite_date" sortColumn={sortColumn} sortOrder={sortOrder} />
                  </div>
                </th>
                {isPreScreening ? (
                  <th
                    className={thSortClass}
                    data-testid={`${testIdPrefix}-table-sort-rent-amount`}
                    onClick={() => onSort("rent_amount")}
                  >
                    <div className="flex items-center gap-2">
                      Expected Rent Amount
                      <ApplicantApplicationsTableSortIcon column="rent_amount" sortColumn={sortColumn} sortOrder={sortOrder} />
                    </div>
                  </th>
                ) : (
                  <>
                    <th
                      className={thSortClass}
                      data-testid={`${testIdPrefix}-table-sort-property-name`}
                      onClick={() => onSort("property_name")}
                    >
                      <div className="flex items-center gap-2">
                        Property Name
                        <ApplicantApplicationsTableSortIcon column="property_name" sortColumn={sortColumn} sortOrder={sortOrder} />
                      </div>
                    </th>
                    <th className={thClass}>Property Address</th>
                    <th
                      className={thSortClass}
                      data-testid={`${testIdPrefix}-table-sort-unit-number`}
                      onClick={() => onSort("unit_number")}
                    >
                      <div className="flex items-center gap-2">
                        Unit Number
                        <ApplicantApplicationsTableSortIcon column="unit_number" sortColumn={sortColumn} sortOrder={sortOrder} />
                      </div>
                    </th>
                  </>
                )}
                <th
                  className={thSortClass}
                  data-testid={`${testIdPrefix}-table-sort-lease-start`}
                  onClick={() => onSort("lease_start_date")}
                >
                  <div className="flex items-center gap-2">
                    {isPreScreening ? "Expected Lease Start Date" : "Lease Start Date"}
                    <ApplicantApplicationsTableSortIcon column="lease_start_date" sortColumn={sortColumn} sortOrder={sortOrder} />
                  </div>
                </th>
                {isPreScreening && (
                  <>
                    <th
                      className={thSortClass}
                      data-testid={`${testIdPrefix}-table-sort-lease-duration`}
                      onClick={() => onSort("lease_duration")}
                    >
                      <div className="flex items-center gap-2">
                        Lease Duration
                        <ApplicantApplicationsTableSortIcon column="lease_duration" sortColumn={sortColumn} sortOrder={sortOrder} />
                      </div>
                    </th>
                    <th
                      className={thSortClass}
                      data-testid={`${testIdPrefix}-table-sort-income-verified`}
                      onClick={() => onSort("income_verified")}
                    >
                      <div className="flex items-center gap-2">
                        Income Verified
                        <ApplicantApplicationsTableSortIcon column="income_verified" sortColumn={sortColumn} sortOrder={sortOrder} />
                      </div>
                    </th>
                  </>
                )}
                <th className={thClass}>Mobile No.</th>
                <th
                  className={thSortClass}
                  data-testid={`${testIdPrefix}-table-sort-status`}
                  onClick={() => onSort("status")}
                >
                  <div className="flex items-center gap-2">
                    Status
                    <ApplicantApplicationsTableSortIcon column="status" sortColumn={sortColumn} sortOrder={sortOrder} />
                  </div>
                </th>
                <th className={thClass}>Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {isLoading ? (
                <tr>
                  <td colSpan={columnCount} className="px-4 sm:px-6 py-8 sm:py-12 text-center">
                    <div className="flex flex-col items-center justify-center gap-3">
                      <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
                      <span className="text-gray-600 font-medium">Loading applications...</span>
                    </div>
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={columnCount} className="px-4 sm:px-6 py-8 sm:py-12 text-center">
                    <div className="flex flex-col items-center justify-center gap-3">
                      <FileText className="h-12 w-12 text-gray-400" />
                      <p className="text-gray-500 font-medium">{emptyTitle}</p>
                      <p className="text-sm text-gray-400">{emptySubtitle}</p>
                    </div>
                  </td>
                </tr>
              ) : (
                rows.map((row, index) => {
                  const rowTestSuffix = `${String(getRowKey(row))}-${index}`;
                  return (
                    <tr
                      key={rowTestSuffix}
                      data-testid={`${testIdPrefix}-table-row-${rowTestSuffix}`}
                      className="hover:bg-blue-50/50 transition-colors border-b border-gray-100 last:border-b-0"
                    >
                      <td className="px-4 sm:px-6 py-3 sm:py-4 whitespace-nowrap text-sm text-gray-900 font-medium">
                        {row.inviteDate}
                      </td>
                      {isPreScreening ? (
                        <td className="px-4 sm:px-6 py-3 sm:py-4 whitespace-nowrap text-sm font-semibold text-gray-900">
                          {row.rentAmountDisplay || "-"}
                        </td>
                      ) : (
                        <>
                          <td className="px-4 sm:px-6 py-3 sm:py-4 whitespace-nowrap text-sm font-semibold text-gray-900">
                            {row.propertyName}
                          </td>
                          <td className="px-4 sm:px-6 py-3 sm:py-4 text-sm text-gray-700 max-w-xs min-w-0">
                            <div className="truncate" title={row.propertyAddress}>
                              {row.propertyAddress || "-"}
                            </div>
                          </td>
                          <td className="px-4 sm:px-6 py-3 sm:py-4 whitespace-nowrap text-sm text-gray-700">
                            {row.unitNumber || ""}
                          </td>
                        </>
                      )}
                      <td className="px-4 sm:px-6 py-3 sm:py-4 whitespace-nowrap text-sm text-gray-900 font-medium">
                        {row.leaseStartDate}
                      </td>
                      {isPreScreening && (
                        <>
                          <td className="px-4 sm:px-6 py-3 sm:py-4 whitespace-nowrap text-sm text-gray-900 font-medium">
                            {row.leaseDurationDisplay || "-"}
                          </td>
                          <td className="px-4 sm:px-6 py-3 sm:py-4 whitespace-nowrap text-sm text-gray-900 font-medium">
                            {row.incomeVerifiedDisplay || "-"}
                          </td>
                        </>
                      )}
                      <td className="px-4 sm:px-6 py-3 sm:py-4 whitespace-nowrap text-sm text-gray-700">
                        {row.mobileNo}
                      </td>
                      <StatusCell
                        row={row}
                        testIdPrefix={testIdPrefix}
                        rowTestSuffix={rowTestSuffix}
                        onShowDenialReasons={onShowDenialReasons}
                      />
                      <ActionsCell
                        row={row}
                        testIdPrefix={testIdPrefix}
                        rowTestSuffix={rowTestSuffix}
                        loadingRowId={loadingRowId}
                        isAnyButtonLoading={isAnyButtonLoading}
                        getRowKey={getRowKey}
                        getButtonConfig={getButtonConfig}
                        onStartContinue={onStartContinue}
                        onView={onView}
                      />
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}
